/**
 * NuevoPage — Formulario de creación de charter.
 * Flujo: elegir catálogo de alquiler → embarcación asociada → fecha → verificar
 * disponibilidad → datos del cliente → estado inicial (presupuesto/confirmada).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Grid, Alert,
  CircularProgress, Chip, Divider, InputAdornment, Snackbar,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

async function getAuthHeaders() { return getFirebaseAuthHeaders(); }
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const initialForm = {
  alquiler_id: '',
  embarcacion_id: '',
  fecha_alquiler: '',
  hora_zarpe: '08:00',
  hora_regreso: '17:00',
  cliente_nombre: '',
  cliente_whatsapp: '',
  cliente_correo: '',
  cliente_identificacion: '',
  adultos: 1,
  ninos: 0,
  bebes: 0,
  estado_inicial: 'presupuesto',
  punto_embarque_id: '',
  descuento_comercial: '',
  descuento_comercial_motivo: '',
  // 'valor' = pesos absolutos; 'porcentaje' = % del precio del catálogo.
  // El backend siempre recibe descuento en pesos (calculado abajo).
  descuento_tipo: 'valor',
  notas: '',
};

export default function NuevoPage() {
  const navigate = useNavigate();
  const { empresaId, refreshCounts } = useOutletContext() || {};
  const [form, setForm] = useState(initialForm);
  const [alquileres, setAlquileres] = useState([]);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [puntos, setPuntos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disp, setDisp] = useState(null); // { disponible, motivo } o null
  const [dispLoading, setDispLoading] = useState(false);
  const [dispBulk, setDispBulk] = useState({});  // { embarcacion_id: {disponible, motivo} }
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const alquilerSelected = useMemo(
    () => alquileres.find(a => a.id === Number(form.alquiler_id)),
    [alquileres, form.alquiler_id]
  );
  const embarcacionSelected = useMemo(
    () => embarcaciones.find(e => e.id === Number(form.embarcacion_id)),
    [embarcaciones, form.embarcacion_id]
  );
  const capacidadOK = useMemo(() => {
    if (!embarcacionSelected) return true;
    const pax = Number(form.adultos) + Number(form.ninos) + Number(form.bebes);
    return pax <= (embarcacionSelected.capacidad_total || 0);
  }, [embarcacionSelected, form.adultos, form.ninos, form.bebes]);

  const cargarCatalogos = useCallback(async () => {
    setLoading(true);
    try {
      const [alqR, embR, ptoR] = await Promise.all([
        fetch(`${API_BASE}/turismo/alquiler-embarcacion?empresa_id=${empresaId}&solo_activos=true`,
          { headers: await getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`,
          { headers: await getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/puntos-embarque?empresa_id=${empresaId}`,
          { headers: await getAuthHeaders() }),
      ]);
      if (alqR.ok) setAlquileres(await alqR.json());
      if (embR.ok) setEmbarcaciones(await embR.json());
      if (ptoR.ok) setPuntos(await ptoR.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId]);

  // Bulk disponibilidad: cuando cambia la fecha, chequea TODAS las embarcaciones
  // para poder pintar chip verde/rojo en cada opción del dropdown.
  useEffect(() => {
    if (!form.fecha_alquiler || !empresaId) { setDispBulk({}); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_BASE}/turismo/charter/disponibilidad/dia?fecha=${form.fecha_alquiler}&empresa_id=${empresaId}`,
          { headers: await getAuthHeaders() }
        );
        if (r.ok) {
          const data = await r.json();
          const map = {};
          (Array.isArray(data) ? data : []).forEach(d => {
            map[d.embarcacion_id] = { disponible: d.disponible, motivo: d.motivo };
          });
          setDispBulk(map);
        }
      } catch (e) { console.error('[disp bulk]', e); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.fecha_alquiler, empresaId]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);

  // Cuando cambia el alquiler, pre-seleccionar su embarcación
  useEffect(() => {
    if (alquilerSelected?.embarcacion_id) {
      setForm(p => ({ ...p, embarcacion_id: alquilerSelected.embarcacion_id }));
    }
  }, [alquilerSelected]);

  // Verificar disponibilidad cuando cambien embarcación + fecha
  const verificarDisp = useCallback(async () => {
    if (!form.embarcacion_id || !form.fecha_alquiler) { setDisp(null); return; }
    setDispLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/charter/disponibilidad?embarcacion_id=${form.embarcacion_id}` +
        `&fecha=${form.fecha_alquiler}&empresa_id=${empresaId}`,
        { headers: await getAuthHeaders() }
      );
      if (r.ok) setDisp(await r.json());
      else setDisp(null);
    } catch (e) { console.error(e); setDisp(null); }
    finally { setDispLoading(false); }
  }, [form.embarcacion_id, form.fecha_alquiler, empresaId]);

  useEffect(() => {
    const t = setTimeout(verificarDisp, 300);
    return () => clearTimeout(t);
  }, [verificarDisp]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // Descuento — el usuario lo puede ingresar como valor absoluto o como %.
  // Siempre calculamos el valor absoluto para enviar al backend y para
  // mostrar el "precio final".
  const precioCatalogo = Number(alquilerSelected?.precio_dia || 0);
  const descuentoInput = Number(form.descuento_comercial) || 0;
  const descuentoValor = form.descuento_tipo === 'porcentaje'
    ? Math.round((precioCatalogo * Math.min(descuentoInput, 100)) / 100)
    : descuentoInput;
  const descuentoNum = Math.max(0, Math.min(descuentoValor, precioCatalogo));
  const precioFinal = Math.max(precioCatalogo - descuentoNum, 0);
  const descuentoValido = descuentoInput === 0
    || (descuentoInput > 0
        && descuentoNum <= precioCatalogo
        && (form.descuento_tipo !== 'porcentaje' || descuentoInput <= 100)
        && form.descuento_comercial_motivo.trim().length >= 3);

  const puedeGuardar = form.alquiler_id && form.embarcacion_id && form.fecha_alquiler
    && form.cliente_nombre.trim().length >= 2
    && form.cliente_whatsapp.trim().length >= 6
    && capacidadOK
    && descuentoValido
    && (form.estado_inicial === 'presupuesto' || (disp && disp.disponible));

  const guardar = async () => {
    if (!puedeGuardar || saving) return;
    setSaving(true);
    try {
      const body = {
        alquiler_id: Number(form.alquiler_id),
        embarcacion_id: Number(form.embarcacion_id),
        fecha_alquiler: form.fecha_alquiler,
        hora_zarpe: form.hora_zarpe || null,
        hora_regreso: form.hora_regreso || null,
        punto_embarque_id: form.punto_embarque_id ? Number(form.punto_embarque_id) : null,
        cliente_nombre: form.cliente_nombre.trim(),
        cliente_whatsapp: form.cliente_whatsapp.trim(),
        cliente_correo: form.cliente_correo.trim() || null,
        cliente_identificacion: form.cliente_identificacion.trim() || null,
        adultos: Number(form.adultos) || 1,
        ninos: Number(form.ninos) || 0,
        bebes: Number(form.bebes) || 0,
        estado_inicial: form.estado_inicial,
        notas: form.notas.trim() || null,
        descuento_comercial: descuentoNum > 0 ? descuentoNum : null,
        descuento_comercial_motivo: descuentoNum > 0 ? form.descuento_comercial_motivo.trim() : null,
      };
      const r = await fetch(
        `${API_BASE}/turismo/charter/reservas?empresa_id=${empresaId}`,
        { method: 'POST', headers: await getAuthHeaders(), body: JSON.stringify(body) }
      );
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail?.message || data?.detail || 'Error al crear charter';
        setSnack({ open: true, msg: typeof msg === 'string' ? msg : JSON.stringify(msg), severity: 'error' });
        return;
      }
      setSnack({ open: true, msg: `Charter creado: ${data.codigo_reserva}`, severity: 'success' });
      refreshCounts?.();
      setTimeout(() => navigate(`/charter/${data.id}`), 700);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    } finally { setSaving(false); }
  };

  const dispChip = () => {
    if (!form.embarcacion_id || !form.fecha_alquiler) {
      return <Chip icon={<HelpOutlineIcon />} label="Selecciona embarcación y fecha" size="small"
        sx={{ bgcolor: '#f1f5f9', color: '#64748b' }} />;
    }
    if (dispLoading) return <Chip icon={<CircularProgress size={12} />} label="Verificando…" size="small" />;
    if (!disp) return null;
    if (disp.disponible) {
      return <Chip icon={<CheckCircleIcon />} label="Disponible ese día" size="small"
        sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />;
    }
    const m = disp.motivo || {};
    const detalle = m.tipo === 'pasadia'
      ? `Ocupada por pasadía #${m.salida_id} · ${m.paquete || ''}`
      : `Ocupada por charter ${m.codigo || `#${m.reserva_id}`} · ${m.cliente || ''}`;
    return <Chip icon={<ErrorIcon />} label={detalle} size="small"
      sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, maxWidth: 480 }} />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/charter/listado')}
          sx={{ textTransform: 'none', color: '#64748b' }}>
          Volver
        </Button>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Nuevo charter
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Alquiler completo de embarcación. Precio del catálogo · anti-overbooking al confirmar.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#7c3aed' }} />
        </Box>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 900 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            1 · Servicio y fecha
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" select label="Catálogo de alquiler *"
                value={form.alquiler_id} onChange={set('alquiler_id')}
              >
                {alquileres.length === 0 && (
                  <MenuItem value="" disabled>Sin catálogos activos</MenuItem>
                )}
                {alquileres.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.nombre} · {fmtMoney(a.precio_dia)} · {a.embarcacion?.nombre || 'sin bote'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" select label="Embarcación *"
                value={form.embarcacion_id} onChange={set('embarcacion_id')}
                helperText={
                  form.fecha_alquiler
                    ? (embarcacionSelected ? `Capacidad ${embarcacionSelected.capacidad_total} pax` : '')
                    : 'Selecciona fecha primero para ver disponibilidad'
                }
              >
                {embarcaciones.map(e => {
                  const d = dispBulk[e.id];
                  const tieneFecha = !!form.fecha_alquiler;
                  return (
                    <MenuItem key={e.id} value={e.id}
                      disabled={tieneFecha && d && !d.disponible && form.estado_inicial === 'confirmada'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>{e.nombre} · {e.capacidad_total} pax</Box>
                        {tieneFecha && d && (
                          <Chip
                            size="small"
                            label={d.disponible ? 'Disponible' : (d.motivo?.tipo === 'pasadia' ? 'Pasadía' : 'Charter')}
                            sx={{
                              height: 18, fontSize: 9.5, fontWeight: 700,
                              bgcolor: d.disponible ? '#dcfce7' : (d.motivo?.tipo === 'pasadia' ? '#dbeafe' : '#ede9fe'),
                              color: d.disponible ? '#166534' : (d.motivo?.tipo === 'pasadia' ? '#1e40af' : '#5b21b6'),
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" type="date" label="Fecha del charter *"
                InputLabelProps={{ shrink: true }}
                value={form.fecha_alquiler} onChange={set('fecha_alquiler')}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth size="small" type="time" label="Hora zarpe"
                InputLabelProps={{ shrink: true }}
                value={form.hora_zarpe} onChange={set('hora_zarpe')}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth size="small" type="time" label="Hora regreso"
                InputLabelProps={{ shrink: true }}
                value={form.hora_regreso} onChange={set('hora_regreso')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" select label="Punto de embarque"
                value={form.punto_embarque_id} onChange={set('punto_embarque_id')}
                helperText="Muelle desde donde zarpa el charter (opcional al presupuestar)"
              >
                <MenuItem value=""><em>Sin definir aún</em></MenuItem>
                {puntos.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}{p.direccion ? ` · ${p.direccion}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Disponibilidad:
                </Typography>
                {dispChip()}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            2 · Cliente y grupo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Nombre del cliente *"
                value={form.cliente_nombre} onChange={set('cliente_nombre')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="WhatsApp *"
                value={form.cliente_whatsapp} onChange={set('cliente_whatsapp')}
                placeholder="+57 300..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Correo (opcional)"
                value={form.cliente_correo} onChange={set('cliente_correo')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Identificación (opcional)"
                value={form.cliente_identificacion} onChange={set('cliente_identificacion')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small" type="number" label="Adultos"
                inputProps={{ min: 1, max: 100 }}
                value={form.adultos} onChange={set('adultos')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small" type="number" label="Niños"
                inputProps={{ min: 0, max: 100 }}
                value={form.ninos} onChange={set('ninos')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth size="small" type="number" label="Bebés"
                inputProps={{ min: 0, max: 100 }}
                value={form.bebes} onChange={set('bebes')}
              />
            </Grid>
            {!capacidadOK && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ fontSize: 12 }}>
                  Pax total ({Number(form.adultos) + Number(form.ninos) + Number(form.bebes)}) supera
                  la capacidad de la embarcación ({embarcacionSelected?.capacidad_total}).
                </Alert>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            3 · Estado inicial y notas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" select label="Estado inicial"
                value={form.estado_inicial} onChange={set('estado_inicial')}
                helperText={form.estado_inicial === 'presupuesto'
                  ? 'Presupuesto NO reserva la embarcación. Fernando puede armar varios.'
                  : 'Confirmada bloquea la embarcación para ese día.'}
              >
                <MenuItem value="presupuesto">Presupuesto</MenuItem>
                <MenuItem value="confirmada">Confirmada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label="Precio del catálogo"
                value={fmtMoney(alquilerSelected?.precio_dia || 0)}
                InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">💰</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select size="small" label="Tipo"
                  value={form.descuento_tipo} onChange={set('descuento_tipo')}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="valor">Valor ($)</MenuItem>
                  <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
                </TextField>
                <TextField
                  fullWidth size="small" type="number" label="Descuento comercial (opcional)"
                  value={form.descuento_comercial} onChange={set('descuento_comercial')}
                  inputProps={form.descuento_tipo === 'porcentaje'
                    ? { min: 0, max: 100, step: 1 }
                    : { min: 0, max: precioCatalogo, step: 10000 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      {form.descuento_tipo === 'porcentaje' ? '%' : '$'}
                    </InputAdornment>,
                  }}
                  helperText={
                    descuentoInput > 0
                      ? `Descuento: ${fmtMoney(descuentoNum)}${form.descuento_tipo === 'porcentaje' ? ` (${descuentoInput}%)` : ''} · Precio final: ${fmtMoney(precioFinal)}`
                      : 'Sin descuento'
                  }
                  error={descuentoInput > 0 && !descuentoValido}
                />
              </Box>
            </Grid>
            {descuentoNum > 0 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" required
                  label="Motivo del descuento *"
                  value={form.descuento_comercial_motivo}
                  onChange={set('descuento_comercial_motivo')}
                  placeholder="Cliente frecuente, temporada baja, cortesía, etc."
                  error={descuentoNum > 0 && form.descuento_comercial_motivo.trim().length < 3}
                  helperText={descuentoNum > 0 && form.descuento_comercial_motivo.trim().length < 3
                    ? 'Motivo requerido cuando hay descuento (mín. 3 caracteres)'
                    : 'Queda registrado en auditoría'}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Notas (opcional)"
                value={form.notas} onChange={set('notas')}
                placeholder="Requisitos especiales, acuerdos verbales, etc."
              />
            </Grid>
          </Grid>

          {form.estado_inicial === 'confirmada' && disp && !disp.disponible && (
            <Alert severity="error" sx={{ mt: 2 }}>
              No puedes confirmar: la embarcación está ocupada ese día. Cambia a
              "Presupuesto" o elige otra fecha/embarcación.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
            <Button onClick={() => navigate('/charter/listado')} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained" startIcon={<SaveIcon />}
              onClick={guardar} disabled={!puedeGuardar || saving}
              sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
                fontWeight: 700, textTransform: 'none',
                '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)' },
              }}
            >
              {saving ? 'Guardando…' : `Crear como ${form.estado_inicial}`}
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
